import { enableProdMode, importProvidersFrom, ErrorHandler, NgZone, provideAppInitializer } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import { SortablejsModule } from '@dustfoundation/ngx-sortablejs';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Location } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_RIPPLE_GLOBAL_OPTIONS,
	RippleGlobalOptions
} from '@angular/material/core';
import { COPO_DATE_FORMATS } from '@copo/mat-datepicker-new';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from '@angular/material/button-toggle';

import { APPLICATION_INSIGHTS_ENDPOINT, APPLICATION_INSIGHTS_KEY, PROD_MODE } from './config';
import { ConnectionInterceptor } from './shared/services/connection.service';
import { AppDateAdapter } from './shared/utils/date.adapter';
import { DateFormats } from './shared/utils/date.format';
import { GlobalErrorHandler } from './shared/utils/error-handler';
import { onAppModuleCreated } from './app-module-created';
import { AppComponent } from './app.component';
import { StaticLoader, StaticMissingTranslationHandler } from './i18n';
import { routing } from './app.routes';
import { CustomLocation } from './shared/services/location.custom';
import { beforeStart } from './before-start';

if (PROD_MODE) {
	enableProdMode();
}

async function startApplication() {
	const application = await bootstrapApplication(AppComponent, {
		providers: [
			importProvidersFrom(
				// those imports are needed for uses in services

				MatSnackBarModule,

				MatDialogModule,
				SortablejsModule.forRoot({ animation: 175 }),
				routing,
				TranslateModule.forRoot({
					loader: { provide: TranslateLoader, useClass: StaticLoader },
					missingTranslationHandler: {
						provide: MissingTranslationHandler, useClass: StaticMissingTranslationHandler
					}
				}),
			),
			{ provide: Location, useClass: CustomLocation },
			provideAppInitializer(async () => beforeStart()),
			provideAnimations(),
			provideHttpClient(withInterceptorsFromDi()),
			{ provide: HTTP_INTERCEPTORS, useClass: ConnectionInterceptor, multi: true },
			{ provide: DateAdapter, useClass: AppDateAdapter },
			{ provide: MAT_DATE_FORMATS, useClass: DateFormats },
			{ provide: COPO_DATE_FORMATS, useClass: DateFormats },
			{ provide: ErrorHandler, useClass: GlobalErrorHandler },
			{
				provide: MAT_RIPPLE_GLOBAL_OPTIONS,
				useValue: { terminateOnPointerUp: true } satisfies RippleGlobalOptions
			},
			{
				provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
				useValue: {
					hideMultipleSelectionIndicator: true,
					hideSingleSelectionIndicator: true
				} satisfies MatButtonToggleDefaultOptions
			},
			{
				provide: ApplicationInsights,
				useFactory: (zone: NgZone) =>
					// must be run ouside angular zone to prevent pooling to call change detection
					zone.runOutsideAngular(() => new ApplicationInsights({
						config: {
							instrumentationKey: APPLICATION_INSIGHTS_KEY,
							endpointUrl: APPLICATION_INSIGHTS_ENDPOINT
						}
					}).loadAppInsights()),
				deps: [NgZone]
			},
		]
	});
	onAppModuleCreated(application);
}

startApplication()
	.catch((error: unknown) => {
		console.error('error awaiting app start', error);
	});
